import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import gibletLogo from '@/assets/giblet-logo.png';
import { format, addDays } from 'date-fns';

export default function DemoCallForDestructionDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  const [title, setTitle] = useState('Outdated OSHA Standards in Construction Safety Blueprint KB');
  const [requestedAction, setRequestedAction] = useState('update');
  const [impactAssessment, setImpactAssessment] = useState(
    'If the outdated OSHA references remain in the Construction Safety Blueprint, construction teams relying on this KB for compliance guidance may implement PPE programs that fail to meet 2024 OSHA Final Rule requirements. This creates direct liability exposure for CURT member organizations and could result in OSHA citations, workplace injuries, and legal consequences.'
  );
  const [proposedResolution, setProposedResolution] = useState(
    'Update Section 3.2 to reference:\n1. ANSI/ISEA Z89.1-2024 for hard hat classifications\n2. Updated 29 CFR 1926.502 fall protection guidance (2023 revision)\n3. Current OSHA APF tables for respiratory protection\n\nRecommend issuing a CfK for a comprehensive safety standards review across all sections.'
  );

  const deadline = format(addDays(new Date(), 7), 'MMMM d, yyyy');

  const handleNext = () => {
    navigate('/demo-call-for-destruction/review', {
      state: {
        ...location.state,
        cfdTitle: title,
        requestedAction,
        impactAssessment,
        proposedResolution,
        deadline,
      }
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">Destruction Request Details</h1>
          <p className="text-lg text-muted-foreground">
            Define the requested action and its impact
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-5">
          <div className="space-y-2">
            <Label>CfD Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>Requested Action</Label>
            <RadioGroup value={requestedAction} onValueChange={setRequestedAction} className="grid grid-cols-2 gap-2">
              {[
                { value: 'remove', label: 'Remove', desc: 'Completely remove from platform' },
                { value: 'update', label: 'Update', desc: 'Require content correction' },
                { value: 'restrict', label: 'Restrict Access', desc: 'Limit visibility with warning' },
                { value: 'disclaimer', label: 'Add Disclaimer', desc: 'Flag as potentially outdated' },
              ].map((a) => (
                <div key={a.value} className="flex items-start space-x-2 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value={a.value} id={`action-${a.value}`} className="mt-0.5" />
                  <Label htmlFor={`action-${a.value}`} className="cursor-pointer">
                    <div className="font-semibold text-foreground text-sm">{a.label}</div>
                    <div className="text-xs text-muted-foreground">{a.desc}</div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Response Deadline</Label>
            <Input value={deadline} disabled />
            <p className="text-xs text-muted-foreground">7 days from submission date</p>
          </div>

          <div className="space-y-2">
            <Label>Impact Assessment</Label>
            <Textarea value={impactAssessment} onChange={(e) => setImpactAssessment(e.target.value)} rows={4} />
          </div>

          <div className="space-y-2">
            <Label>Proposed Resolution</Label>
            <Textarea value={proposedResolution} onChange={(e) => setProposedResolution(e.target.value)} rows={5} />
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/demo-call-for-destruction/bounty')} className="min-w-[120px]">
            Back
          </Button>
          <Button onClick={handleNext} disabled={!title} className="min-w-[120px]">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
